/**
 * UK income tax, National Insurance and student loan, 2026/27. Checked 2026-10-11 against HMRC / gov.scot / student loan
 * threshold tables (Scottish bands from the Scottish Budget of 13 Jan 2026). Review every April.
 */
import type { ParsedTaxCode } from "./ukCode";

export const UK_YEAR_LABEL = "2026/27";

const PA = 12570;
const TAPER_START = 100000;
const BASIC_BAND = 37700;
const ADDITIONAL_AT = 125140;

/** Scottish bands as widths of taxable income (above the personal allowance); advanced -> top at total income 125,140. */
const SCOT_BANDS = [
  { upTo: 3967, rate: 0.19 }, { upTo: 16956, rate: 0.2 }, { upTo: 31092, rate: 0.21 }, { upTo: 62430, rate: 0.42 },
];

const NI_PT = 12570, NI_UEL = 50270, NI_MAIN = 0.08, NI_UPPER = 0.02;
export const EMPLOYER_NI = { rate: 0.15, threshold: 5000 };

export type LoanPlan = "none" | "plan1" | "plan2" | "plan4" | "plan5";
export const LOAN_PLANS: Record<Exclude<LoanPlan, "none">, { label: string; threshold: number }> = {
  plan1: { label: "Plan 1", threshold: 26900 }, plan2: { label: "Plan 2", threshold: 29385 }, plan4: { label: "Plan 4 (Scotland)", threshold: 33795 }, plan5: { label: "Plan 5", threshold: 25000 },
};
export const POSTGRAD = { threshold: 21000, rate: 0.06 };
export const LOAN_RATE = 0.09;

export type PensionType = "sacrifice" | "netpay" | "ras";

export function personalAllowance(adjustedNetIncome: number): number {
  if (adjustedNetIncome <= TAPER_START) return PA;
  return Math.max(0, PA - Math.floor((adjustedNetIncome - TAPER_START) / 2));
}

/** Marriage Allowance (GOV.UK, "Marriage Allowance: How it works"): £1,260 of allowance moves from the lower earner; the receiver's tax falls by up to £252. */
export const MARRIAGE_ALLOWANCE = { transfer: 1260, reduction: 252, basicRateTop: 50270, scottishIntermediateTop: 43662 };
export type MarriageChoice = "none" | "receive" | "give";

export interface UkInput {
  salary: number;
  scotland: boolean;
  pensionPct: number;
  pensionType: PensionType;
  loan: LoanPlan;
  postgrad: boolean;
  /** Extra pay in the year (a one-off bonus or overtime). Taxed and charged National Insurance and student loan like pay; pension % is taken on salary only. */
  bonus?: number;
  /** Taxable benefits in kind: added to taxable pay for income tax, no employee National Insurance (B-0129). */
  benefits?: number;
  /** A parsed tax code (ukCode.ts). Without one, the standard allowance and its taper are used exactly as before. */
  code?: ParsedTaxCode;
  /** Used only when no tax code is given: the code already contains Marriage Allowance (M or N). */
  marriage?: MarriageChoice;
}
export interface UkResult {
  incomeTax: number; ni: number; studentLoan: number; pensionEmployee: number; pensionIntoPot: number;
  takeHome: number; allowance: number; taxable: number;
  bands: { label: string; rate: number; amount: number; tax: number }[];
  niMain: number; niUpper: number; employerNi: number; effectiveMarginal60: boolean;
  /** B-0129 (additive): the tax code and Marriage Allowance in effect, and the region actually used. */
  scotlandUsed: boolean; marriageReduction: number; marriageNote: string; benefitsTaxable: number; codeNote: string;
}

export function computeUk(i: UkInput): UkResult {
  const baseSalary = Math.max(0, i.salary);
  const bonus = Math.max(0, i.bonus ?? 0);
  const benefits = Math.max(0, i.benefits ?? 0);
  const salary = baseSalary + bonus; // all pay in the year
  const c = baseSalary * (Math.min(Math.max(i.pensionPct, 0), 100) / 100); // pension is taken on salary, not on the bonus
  const gross = i.pensionType === "ras" ? c / 0.8 : 0;
  const taxPay = (i.pensionType === "ras" ? salary : salary - c) + benefits; // net pay and sacrifice both reduce taxable pay; benefits add to it
  const ani = taxPay - gross;
  const code = i.code && i.code.raw !== "" && i.code.valid ? i.code : undefined;
  const scotland = i.scotland || code?.region === "scotland";
  let marriageNote = "", marriageReduction = 0, codeNote = "";
  let allowance = personalAllowance(ani);
  if (code) {
    allowance = code.kind === "nt" ? 0 : code.allowance; // a code replaces the allowance and its taper: HMRC builds any taper into the code
    codeNote = code.description;
  } else if (i.marriage === "give") {
    if (ani <= PA) { allowance = PA - MARRIAGE_ALLOWANCE.transfer; marriageNote = `Marriage Allowance given away: allowance reduced by ${MARRIAGE_ALLOWANCE.transfer.toLocaleString("en-GB")} pounds.`; }
    else marriageNote = "Marriage Allowance can only be given away if your income is at or below the personal allowance (12,570 pounds), so it was not applied.";
  }
  const taxable = code?.kind === "nt" ? 0 : Math.max(0, taxPay - allowance);
  const ext = gross;

  const bands: UkResult["bands"] = [];
  let tax = 0;
  const push = (label: string, rate: number, amount: number) => { if (amount > 0) { bands.push({ label, rate, amount, tax: amount * rate }); tax += amount * rate; } };
  if (code?.kind === "flat") {
    push(`${code.flatLabel![0].toUpperCase()}${code.flatLabel!.slice(1)} on all pay`, code.flatRate! / 100, taxable);
  } else if (!scotland) {
    const B = BASIC_BAND + ext, H = ADDITIONAL_AT + ext;
    push("Basic rate", 0.2, Math.min(taxable, B));
    push("Higher rate", 0.4, Math.max(0, Math.min(taxable, H) - B));
    push("Additional rate", 0.45, Math.max(0, taxable - H));
  } else {
    const names = ["Starter", "Basic", "Intermediate", "Higher"];
    let last = 0;
    SCOT_BANDS.forEach((b, k) => { push(`${names[k]} rate`, b.rate, Math.max(0, Math.min(taxable, b.upTo + ext) - last)); last = b.upTo + ext; });
    const topCut = Math.max(last, ADDITIONAL_AT - allowance + ext);
    push("Advanced rate", 0.45, Math.max(0, Math.min(taxable, topCut) - last));
    push("Top rate", 0.48, Math.max(0, taxable - topCut));
  }
  if (!code && i.marriage === "receive") {
    const topOfBand = scotland ? MARRIAGE_ALLOWANCE.scottishIntermediateTop : MARRIAGE_ALLOWANCE.basicRateTop;
    if (ani > PA && ani <= topOfBand) {
      marriageReduction = Math.min(MARRIAGE_ALLOWANCE.reduction, tax);
      tax -= marriageReduction;
      marriageNote = `Marriage Allowance received: income tax reduced by ${marriageReduction.toLocaleString("en-GB", { maximumFractionDigits: 2 })} pounds (up to ${MARRIAGE_ALLOWANCE.reduction}).`;
    } else marriageNote = `Marriage Allowance can only be received if you pay tax at the basic rate (${scotland ? "starter, basic or intermediate rate in Scotland" : "income up to 50,270 pounds"}), so it was not applied.`;
  }
  if (code && i.marriage && i.marriage !== "none") marriageNote = "A tax code was entered, so the Marriage Allowance choice was ignored: the code already includes it (M or N).";

  const niPay = i.pensionType === "sacrifice" ? salary - c : salary; // only salary sacrifice lowers NI
  const niMain = niPay > NI_PT ? (Math.min(niPay, NI_UEL) - NI_PT) * NI_MAIN : 0;
  const niUpper = niPay > NI_UEL ? (niPay - NI_UEL) * NI_UPPER : 0;
  const ni = niMain + niUpper;

  const slPay = i.pensionType === "sacrifice" ? salary - c : salary;
  let sl = 0;
  if (i.loan !== "none") sl += Math.max(0, slPay - LOAN_PLANS[i.loan].threshold) * LOAN_RATE;
  if (i.postgrad) sl += Math.max(0, slPay - POSTGRAD.threshold) * POSTGRAD.rate;

  const takeHome = salary - c - tax - ni - sl;
  return {
    incomeTax: tax, ni, studentLoan: sl, pensionEmployee: c, pensionIntoPot: i.pensionType === "ras" ? gross : c,
    takeHome, allowance, taxable, bands, niMain, niUpper, employerNi: Math.max(0, niPay - EMPLOYER_NI.threshold) * EMPLOYER_NI.rate,
    effectiveMarginal60: !code && ani > TAPER_START && ani < ADDITIONAL_AT,
    scotlandUsed: scotland, marriageReduction, marriageNote, benefitsTaxable: benefits, codeNote,
  };
}
